import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddPendingEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #pillObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddPendingEvent.eventName, this.#handleCartAddPending);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    this.#pillObserver = new MutationObserver(this.#handleAddedNodes);
    this.#pillObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddPendingEvent.eventName, this.#handleCartAddPending);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    this.#pillObserver?.disconnect();
    this.#pillObserver = null;
  }

  /**
   * Nundies hand-drawn CTAs added by a morph or the optimistic summary need their pill laid out.
   * @param {MutationRecord[]} records
   */
  #handleAddedNodes = (records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (!node.matches('[data-nundies-pill]') && !node.querySelector('[data-nundies-pill]')) continue;

        document.dispatchEvent(
          new CustomEvent('theme:dynamic-content', {
            bubbles: true,
            detail: { scope: node },
          })
        );
      }
    }
  };

  // Open immediately when add-to-cart is initiated — before the network request.
  #handleCartAddPending = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog({ immediate: true });
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog.bind(this), { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
